/**
 * Financial Aid Value Objects
 *
 * Schemas for 18 sections of the comprehensive financial aid application.
 * This module re-exports from focused sub-modules.
 *
 * @module financial-aid-domain/value-objects/financial-aid
 */
import { z } from 'zod';

// Family Information (Sections 1-3)
export {
  createFinancialAidActionErrorReport,
  FINANCIAL_AID_CYCLE_SETTINGS_UNAVAILABLE_CODE,
  FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODE,
  FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_CODES,
  FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_ERROR_TYPE,
  FINANCIAL_AID_INVITE_CONFIRMATION_UNAVAILABLE_MESSAGE,
  FamilyInformationSchema,
  formatFinancialAidSocialSecurityNumberInput,
  formatMaskedSocialSecurityLast4,
  isFinancialAidInviteConfirmationUnavailableCode,
  isValidFinancialAidSocialSecurityNumber,
  readFinancialAidSocialSecurityLast4,
  readFinancialAidActionErrorMessage,
  readFinancialAidActionErrorReport,
  DraftSecondaryParentGuardianInfoSchema,
  type FinancialAidActionErrorReport,
  type DraftSecondaryParentGuardianInfo,
  type FamilyInformation,
  ParentGuardianInfoSchema,
  type ParentGuardianInfo,
} from './family-info';

// Financial Status (Sections 4-8)
export {
  EmploymentEntrySchema,
  type EmploymentEntry,
  EmploymentInformationSchema,
  type EmploymentInformation,
  IncomeEntrySchema,
  type IncomeEntry,
  IncomeSourcesSchema,
  type IncomeSources,
  AssetEntrySchema,
  type AssetEntry,
  AssetsSchema,
  type Assets,
  LiabilityEntrySchema,
  type LiabilityEntry,
  LiabilitiesSchema,
  type Liabilities,
  ExpenseEntrySchema,
  type ExpenseEntry,
  MedicalCoverageAnswerSchema,
  type MedicalCoverageAnswer,
  MonthlyExpensesSchema,
  type MonthlyExpenses,
} from './financial-status';

// Circumstances (Sections 9-11)
export {
  SpecialCircumstancesSchema,
  type SpecialCircumstances,
  OtherTuitionEntrySchema,
  type OtherTuitionEntry,
  OtherTuitionObligationsSchema,
  type OtherTuitionObligations,
  MajorExpenditureSchema,
  type MajorExpenditure,
  MajorExpendituresSchema,
  type MajorExpenditures,
} from './circumstances';

// Assets Detail (Sections 12-14)
export {
  BusinessOwnershipSchema,
  type BusinessOwnership,
  RealEstatePropertySchema,
  type RealEstateProperty,
  RealEstateSchema,
  type RealEstate,
  VehicleSchema,
  type Vehicle,
  VehiclesSchema,
  type Vehicles,
} from './assets-detail';

// Documents and Certification (Sections 15-17)
export {
  AdditionalInformationSchema,
  type AdditionalInformation,
  DocumentChecklistItemSchema,
  type DocumentChecklistItem,
  DocumentChecklistSchema,
  type DocumentChecklist,
  CertificationSchema,
  type Certification,
} from './documents-certification';

// Review and Students (Section 18 + YST Students)
export {
  ReviewSummarySchema,
  type ReviewSummary,
  YstStudentEntrySchema,
  type YstStudentEntry,
} from './review-students';

// Import schemas for building the composite
import { FamilyInformationSchema } from './family-info';
import {
  DraftSecondaryParentGuardianInfoSchema,
  ParentGuardianInfoSchema,
} from './family-info';
import {
  EmploymentInformationSchema,
  IncomeSourcesSchema,
  AssetsSchema,
  LiabilitiesSchema,
  MonthlyExpensesSchema,
} from './financial-status';
import {
  SpecialCircumstancesSchema,
  OtherTuitionObligationsSchema,
  MajorExpendituresSchema,
} from './circumstances';
import {
  BusinessOwnershipSchema,
  RealEstateSchema,
  VehiclesSchema,
} from './assets-detail';
import {
  AdditionalInformationSchema,
  DocumentChecklistSchema,
  CertificationSchema,
} from './documents-certification';
import {
  ReviewSummarySchema,
  YstStudentEntrySchema,
} from './review-students';

// ============================================================================
// Complete Financial Aid Application Sections
// ============================================================================

export const FinancialAidSectionsSchema = z.object({
  // Section 1
  familyInformation: FamilyInformationSchema,
  // Section 2
  parent1: ParentGuardianInfoSchema,
  // Section 3
  parent2: DraftSecondaryParentGuardianInfoSchema.nullable().optional(),
  // Section 4
  employment: EmploymentInformationSchema,
  // Section 5
  income: IncomeSourcesSchema,
  // Section 6
  assets: AssetsSchema,
  // Section 7
  liabilities: LiabilitiesSchema,
  // Section 8
  expenses: MonthlyExpensesSchema,
  // Section 9
  specialCircumstances: SpecialCircumstancesSchema,
  // Section 10
  otherTuition: OtherTuitionObligationsSchema,
  // Section 11
  majorExpenditures: MajorExpendituresSchema,
  // Section 12
  businessOwnership: BusinessOwnershipSchema,
  // Section 13
  realEstate: RealEstateSchema,
  // Section 14
  vehicles: VehiclesSchema,
  // Section 15
  additionalInfo: AdditionalInformationSchema,
  // Section 16
  documentChecklist: DocumentChecklistSchema,
  // Section 17
  certification: CertificationSchema.nullable().optional(), // Only present when submitted
  // Section 18 (computed)
  reviewSummary: ReviewSummarySchema.nullable().optional(),
  // YST Students for this application
  ystStudents: z.array(YstStudentEntrySchema).default([]),
});
export type FinancialAidSections = z.infer<typeof FinancialAidSectionsSchema>;
